/**
 * DisposalPricesPage — what the company pays each disposal place, and the disposal
 * suggestion switch (Phase 28 / task 28.5a, DEC-331 D-2, D-7, D-8, D-9).
 *
 * Two sections:
 *  1. Suggestions: the on/off switch (default off), what drivers and dispatchers see, and
 *     the typical load used before a load exists. The switch can't be turned on until the
 *     fuel cost and driver cost are set in dispatch Settings → Jobs, products & pricing (D-4); the page says so
 *     rather than offering a switch that will be refused.
 *  2. Prices: every price ever entered, newest first per place, marked "In use" / "Starts
 *     <date>" / "Replaced". Prices are never edited; a new price with a later start date
 *     replaces the old one on its day. Typed dollars accept up to four decimals ($0.425).
 *
 * MANAGE_DISPOSAL_PRICES (tenant admin + billing manager); the nav gate in App.tsx is UX.
 */

import React from 'react';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';

import {
  DISPATCHER_DISPLAY_LABELS,
  DRIVER_DISPLAY_LABELS,
  formatPricePerBbl,
  missingCostsMessage,
  readPriceInput,
  type DispatcherDisplay,
  type DisposalPlace,
  type DisposalPlaceKind,
  type DisposalPrice,
  type DisposalRecSettings,
  type DriverDisplay,
} from '../features/billing/disposal-prices';
import {
  useAddDisposalPrice,
  useDisposalPlaces,
  useDisposalPrices,
  useDisposalRecSettings,
  useSaveDisposalRecSettings,
} from '../features/billing/disposal-prices-hooks';
import { formatCalendarDate, localTodayIso } from './InvoiceDetailPage';
import { useUserTimezone } from '../lib/use-user-timezone';

const helpStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };

/** SWD A2 — which kind a priced place is; a facility needs no note. */
const PLACE_KIND_NOTE: Readonly<Record<DisposalPlaceKind, string>> = {
  FACILITY: '',
  LOCATION: '(your drop-off location)',
  WELL: '(your disposal well)',
};
const labelStyle: React.CSSProperties = { display: 'block', fontWeight: 600, marginBottom: '6px' };
const selectStyle: React.CSSProperties = {
  minHeight: '38px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
};

export function DisposalPricesPage(): React.JSX.Element {
  return (
    <div style={{ padding: '24px', maxWidth: '860px' }}>
      <p style={{ ...helpStyle, marginBottom: '20px' }}>
        These are the prices your company <strong>pays</strong> each disposal place per barrel. They
        are used to suggest the cheapest place for each load. They are never added to customer
        invoices.
      </p>
      <SuggestionsSection />
      <PricesSection />
    </div>
  );
}

// ── 1. Suggestions ───────────────────────────────────────────────────────────────

function SuggestionsSection(): React.JSX.Element {
  const { data, isLoading, isError } = useDisposalRecSettings();
  const save = useSaveDisposalRecSettings();
  const [draft, setDraft] = React.useState<DisposalRecSettings | null>(null);
  const [typical, setTypical] = React.useState('');

  React.useEffect(() => {
    if (data !== undefined) {
      setDraft(data.settings);
      setTypical(String(data.settings.typicalLoadBbl));
    }
  }, [data]);

  if (isLoading || draft === null) {
    if (isError) {
      return (
        <p style={{ color: 'var(--color-danger)' }}>Couldn&apos;t load the suggestion settings.</p>
      );
    }
    return (
      <p role="status" style={helpStyle}>
        Loading…
      </p>
    );
  }
  const saved = data?.settings;
  const missing = missingCostsMessage(draft);
  const typicalNum = /^\d{1,4}$/.test(typical.trim()) ? Number(typical.trim()) : NaN;
  const typicalOk = Number.isInteger(typicalNum) && typicalNum >= 1 && typicalNum <= 1000;
  const changed =
    saved !== undefined &&
    (draft.enabled !== saved.enabled ||
      draft.driverDisplay !== saved.driverDisplay ||
      draft.dispatcherDisplay !== saved.dispatcherDisplay ||
      (typicalOk && typicalNum !== saved.typicalLoadBbl));
  // Turning ON needs both costs; turning off, or anything while off, is always allowed.
  const blockedOn = draft.enabled && saved !== undefined && !saved.enabled && missing !== null;

  return (
    <section style={{ marginBottom: '32px' }}>
      <h2 style={{ fontWeight: 600, marginBottom: '8px' }}>Suggestions</h2>
      <label style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '6px' }}>
        <input
          type="checkbox"
          checked={draft.enabled}
          disabled={!draft.enabled && missing !== null}
          onChange={(e): void => setDraft({ ...draft, enabled: e.target.checked })}
          style={{ width: '20px', height: '20px' }}
        />
        <span style={{ fontWeight: 600 }}>Suggest the cheapest disposal place for each load</span>
      </label>
      <p style={{ ...helpStyle, marginBottom: '14px' }}>
        {draft.enabled
          ? 'Drivers and dispatchers see the places ranked by estimated total cost.'
          : 'Off: drivers see the usual alphabetical list. lodeX still quietly notes what it would have suggested, so you can see the possible savings before turning this on.'}
      </p>
      {missing !== null && (
        <p
          role="note"
          style={{ ...helpStyle, color: 'var(--color-warning)', marginBottom: '14px' }}
        >
          {missing}
        </p>
      )}

      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '14px' }}>
        <div>
          <label htmlFor="driver-display" style={labelStyle}>
            Drivers see
          </label>
          <select
            id="driver-display"
            value={draft.driverDisplay}
            onChange={(e): void =>
              setDraft({ ...draft, driverDisplay: e.target.value as DriverDisplay })
            }
            style={selectStyle}
          >
            {(Object.keys(DRIVER_DISPLAY_LABELS) as DriverDisplay[]).map((k) => (
              <option key={k} value={k}>
                {DRIVER_DISPLAY_LABELS[k]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="dispatcher-display" style={labelStyle}>
            Dispatchers see
          </label>
          <select
            id="dispatcher-display"
            value={draft.dispatcherDisplay}
            onChange={(e): void =>
              setDraft({ ...draft, dispatcherDisplay: e.target.value as DispatcherDisplay })
            }
            style={selectStyle}
          >
            {(Object.keys(DISPATCHER_DISPLAY_LABELS) as DispatcherDisplay[]).map((k) => (
              <option key={k} value={k}>
                {DISPATCHER_DISPLAY_LABELS[k]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="typical-load" style={labelStyle}>
            Typical load (bbl)
          </label>
          <Input
            id="typical-load"
            inputMode="numeric"
            value={typical}
            aria-invalid={!typicalOk}
            onChange={(e): void => setTypical(e.target.value)}
            style={{ maxWidth: '120px' }}
          />
        </div>
      </div>
      <p style={{ ...helpStyle, marginBottom: '14px' }}>
        The typical load is used for a new job&apos;s suggestion when its pickup has no past loads;
        otherwise the average of its last 10 loads is used.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Button
          type="button"
          disabled={!changed || !typicalOk || blockedOn || save.isPending}
          onClick={(): void => {
            if (saved === undefined) return;
            save.mutate({
              ...(draft.enabled !== saved.enabled ? { enabled: draft.enabled } : {}),
              ...(draft.driverDisplay !== saved.driverDisplay
                ? { driverDisplay: draft.driverDisplay }
                : {}),
              ...(draft.dispatcherDisplay !== saved.dispatcherDisplay
                ? { dispatcherDisplay: draft.dispatcherDisplay }
                : {}),
              ...(typicalNum !== saved.typicalLoadBbl ? { typicalLoadBbl: typicalNum } : {}),
            });
          }}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        {!typicalOk && (
          <span role="alert" style={{ fontSize: '13px', color: 'var(--color-danger)' }}>
            Typical load must be a whole number from 1 to 1000.
          </span>
        )}
        {save.isError && (
          <span role="alert" style={{ fontSize: '13px', color: 'var(--color-danger)' }}>
            Couldn&apos;t save. Nothing was changed; try again.
          </span>
        )}
      </div>
    </section>
  );
}

// ── 2. Prices ────────────────────────────────────────────────────────────────────

function status(p: DisposalPrice, today: string): string {
  if (p.isCurrent) return 'In use';
  if (p.effectiveFrom > today) return `Starts ${formatCalendarDate(p.effectiveFrom)}`;
  return 'Replaced';
}

function PricesSection(): React.JSX.Element {
  const { data, isLoading, isError, refetch } = useDisposalPrices();
  return (
    <section>
      <h2 style={{ fontWeight: 600, marginBottom: '8px' }}>Prices you pay</h2>
      <AddPriceForm />
      {isLoading && (
        <p role="status" style={helpStyle}>
          Loading prices…
        </p>
      )}
      {isError && (
        <div style={{ color: 'var(--color-danger)' }}>
          <p style={{ marginBottom: '8px' }}>Couldn&apos;t load prices.</p>
          <Button variant="outline" onClick={(): void => void refetch()}>
            Retry
          </Button>
        </div>
      )}
      {data !== undefined &&
        (data.prices.length === 0 ? (
          <p style={helpStyle}>No prices yet. Add the first one above.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Place</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Starts</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.prices.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.targetName ?? '—'}</TableCell>
                  <TableCell>{formatPricePerBbl(p.rateHundredthsCentPerBbl)}</TableCell>
                  <TableCell>{formatCalendarDate(p.effectiveFrom)}</TableCell>
                  <TableCell>{status(p, data.today)}</TableCell>
                  <TableCell>{p.notes ?? ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ))}
    </section>
  );
}

function AddPriceForm(): React.JSX.Element {
  const tz = useUserTimezone();
  const today = localTodayIso(tz);
  const [query, setQuery] = React.useState('');
  const [place, setPlace] = React.useState<DisposalPlace | null>(null);
  const [price, setPrice] = React.useState('');
  const [starts, setStarts] = React.useState(today);
  const [notes, setNotes] = React.useState('');
  const [done, setDone] = React.useState<string | null>(null);
  const places = useDisposalPlaces(query);
  const add = useAddDisposalPrice(() => {
    setDone(`Saved ${place?.name ?? ''}.`);
    setPrice('');
    setNotes('');
  });

  const parsed = readPriceInput(price);
  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(starts);
  const ready = place !== null && parsed.ok && dateOk && !add.isPending;

  return (
    <div
      style={{
        borderRadius: 'var(--radius-card)',
        backgroundColor: 'var(--color-bg-secondary)',
        padding: '14px',
        marginBottom: '18px',
        display: 'grid',
        gap: '12px',
      }}
    >
      <div>
        <label htmlFor="price-place" style={labelStyle}>
          Place
        </label>
        {place === null ? (
          <>
            <Input
              id="price-place"
              placeholder="Search disposal facilities and your drop-off locations"
              value={query}
              onChange={(e): void => setQuery(e.target.value)}
            />
            <ul
              aria-label="Matching places"
              style={{ listStyle: 'none', padding: 0, margin: '6px 0 0' }}
            >
              {(places.data?.places ?? []).slice(0, 8).map((p) => (
                <li key={`${p.kind}-${p.id}`}>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={(): void => {
                      setPlace(p);
                      setDone(null);
                    }}
                  >
                    {p.name}
                    {PLACE_KIND_NOTE[p.kind] !== '' && (
                      <span style={helpStyle}> {PLACE_KIND_NOTE[p.kind]}</span>
                    )}
                    <span style={{ ...helpStyle, marginLeft: '8px' }}>
                      {p.currentRateHundredthsCentPerBbl === null
                        ? 'no price yet'
                        : `now ${formatPricePerBbl(p.currentRateHundredthsCentPerBbl)}`}
                    </span>
                  </Button>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <strong>{place.name}</strong>
            {PLACE_KIND_NOTE[place.kind] !== '' && (
              <span style={helpStyle}>{PLACE_KIND_NOTE[place.kind]}</span>
            )}
            <Button type="button" variant="ghost" onClick={(): void => setPlace(null)}>
              Change
            </Button>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <div>
          <label htmlFor="price-amount" style={labelStyle}>
            Price per barrel ($)
          </label>
          <Input
            id="price-amount"
            inputMode="decimal"
            placeholder="0.425"
            value={price}
            aria-invalid={price !== '' && !parsed.ok}
            onChange={(e): void => setPrice(e.target.value)}
            style={{ maxWidth: '160px' }}
          />
          {price !== '' && !parsed.ok && (
            <p role="alert" style={{ fontSize: '12px', color: 'var(--color-danger)' }}>
              {parsed.message}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="price-starts" style={labelStyle}>
            Starts on
          </label>
          <input
            id="price-starts"
            type="date"
            value={starts}
            onChange={(e): void => setStarts(e.target.value)}
            style={selectStyle}
          />
        </div>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <label htmlFor="price-notes" style={labelStyle}>
            Notes (optional)
          </label>
          <Input
            className="keep-case"
            id="price-notes"
            value={notes}
            maxLength={500}
            onChange={(e): void => setNotes(e.target.value)}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <Button
          type="button"
          disabled={!ready}
          onClick={(): void => {
            if (place === null || !parsed.ok) return;
            add.mutate({
              target: { kind: place.kind, id: place.id },
              rateHundredthsCentPerBbl: parsed.units,
              effectiveFrom: starts,
              notes: notes.trim() === '' ? null : notes,
            });
          }}
        >
          {add.isPending ? 'Saving…' : 'Add price'}
        </Button>
        {add.isError && (
          <span role="alert" style={{ fontSize: '13px', color: 'var(--color-danger)' }}>
            Couldn&apos;t save. This place may already have a price starting on that date.
          </span>
        )}
        {done !== null && !add.isError && (
          <span role="status" style={helpStyle}>
            {done}
          </span>
        )}
      </div>
    </div>
  );
}
